import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { RecoveryContext } from "../../App";
import { Chess } from "chess.js";
import { Puzzle } from "../../Utils/Puzzle.mjs";
import axios from "../../Utils/axiosConfig";

const calculateNewRating = (currentRating, puzzleRating, success) => {
  const K = 32;
  const expectedScore =
    1 / (1 + Math.pow(10, (puzzleRating - currentRating) / 400));
  const actualScore = success ? 1 : 0;
  const ratingChange = K * (actualScore - expectedScore);

  if (!success && ratingChange > -4) {
    return currentRating - 4;
  }

  if (success && ratingChange > 21) {
    return currentRating + 21;
  }

  return Math.round(currentRating + ratingChange);
};

export const CasualPuzzles = () => {
  const {
    currentFen,
    setCurrentFen,
    username,
    initialOrientation,
    setInitialOrientation,
  } = useContext(RecoveryContext);
  const [puzzleIndex, setPuzzleIndex] = useState(0);
  const [ratingChange, setRatingChange] = useState(0);
  const [userRating, setUserRating] = useState(() => {
    const savedRating = sessionStorage.getItem("userRating");
    return savedRating ? parseInt(savedRating) : 500;
  });
  const [isPuzzleCompleted, setIsPuzzleCompleted] = useState(false);
  const [isPuzzleRated, setIsPuzzleRated] = useState(false);
  const [puzzles, setPuzzles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const chess = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPuzzles = async () => {
      try {
        const response = await axios.get("/puzzles/FetchCasual", {
          timeout: 10000,
        });

        if (response.data && response.data.length > 0) {
          const puzzlesWithFirstMove = response.data.map((puzzle) => ({
            ...puzzle,
            makeFirstMove: true,
          }));
          setPuzzles(puzzlesWithFirstMove);
        } else {
          setError("No puzzles available");
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };
    fetchPuzzles();
  }, []);
  useEffect(() => {
    const fetchUserRating = async () => {
      if (!username) {
        return;
      }

      try {
        const response = await axios.get(`/user-rating/${username}`);
        setUserRating(response.data.rating);
      } catch (error) {
        console.error("Error fetching user rating:", error);
      }
    };

    fetchUserRating();
  }, [username]);

  useEffect(() => {
    if (puzzles.length === 0) return;

    const currentPuzzle = puzzles[puzzleIndex];
    chess.current = new Chess(currentPuzzle.fen);

    const tempGame = new Chess(currentPuzzle.fen);
    const newOrientation = tempGame.turn() === "w" ? "black" : "white";
    const savedOrientation = sessionStorage.getItem("boardOrientation");
    const finalOrientation = savedOrientation || newOrientation;
    setInitialOrientation(finalOrientation);
    sessionStorage.setItem("boardOrientation", finalOrientation);
    setCurrentFen(currentPuzzle.fen);

    if (currentPuzzle.makeFirstMove) {
      const move = chess.current.move(currentPuzzle.moves[0]);
      if (move) {
        const newFen = chess.current.fen();
        setCurrentFen(newFen);
      }
    }

    setRatingChange(0);
    setIsPuzzleCompleted(false);
    setIsPuzzleRated(false);
  }, [puzzleIndex, puzzles, setCurrentFen, setInitialOrientation]);

  useEffect(() => {
    const savedOrientation = sessionStorage.getItem("boardOrientation");
    if (savedOrientation) {
      setInitialOrientation(savedOrientation);
    }
  }, []);
  const handleMove = (from, to) => {
    const move = chess.current.move({ from, to });
    if (move) {
      const newFen = chess.current.fen();
      setCurrentFen(newFen);
    }
  };

  const updateRatingInDatabase = async (newRating) => {
    if (!username) {
      return;
    }

    try {
      const ratingChange = newRating - userRating;
      await axios.post("/update-rating", {
        username,
        ratingChange,
      });
    } catch (error) {
      console.error("Error updating rating:", error);
    }
  };

  const handlePuzzleSolve = () => {
    if (isPuzzleRated || puzzles.length === 0) {
      setIsPuzzleCompleted(true);
      return;
    }
    const newRating = calculateNewRating(
      userRating,
      puzzles[puzzleIndex].rating,
      true
    );
    const change = newRating - userRating;
    setTimeout(() => {
      setUserRating(newRating);
      sessionStorage.setItem("userRating", newRating);
      setIsPuzzleCompleted(true);
      setRatingChange(change);
      setIsPuzzleRated(true);
      updateRatingInDatabase(newRating);
    }, 0);
  };

  const handlePuzzleFail = () => {
    if (isPuzzleRated || puzzles.length === 0) {
      setIsPuzzleCompleted(true);
      return;
    }
    const newRating = calculateNewRating(
      userRating,
      puzzles[puzzleIndex].rating,
      false
    );
    const change = newRating - userRating;
    setTimeout(() => {
      setUserRating(newRating);
      sessionStorage.setItem("userRating", newRating);
      setIsPuzzleCompleted(true);
      setRatingChange(change);
      setIsPuzzleRated(true);
      updateRatingInDatabase(newRating);
    }, 0);
  };

  const customLightSquareStyle = {
    backgroundColor: "#AE8FD6",
  };

  const customDarkSquareStyle = {
    backgroundColor: "#4F3B7B",
  };

  if (isLoading) {
    return <div>Loading puzzles...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (puzzles.length === 0) {
    return <div>No puzzles available</div>;
  }

  const currentPuzzle = puzzles[puzzleIndex];

  return (
    <div className="puzzle-container responsive-page-shell">
      <Puzzle.Root
        puzzle={currentPuzzle}
        onSolve={() => {
          setTimeout(() => {
            handlePuzzleSolve();
          }, 0);
        }}
        onFail={() => {
          setTimeout(() => {
            handlePuzzleFail();
          }, 0);
        }}
      >
        {" "}
        <div
          className="puzzle-board-casual"
          style={{
            width: "min(770px, 100%)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            position: "relative",
            margin: "0",
          }}
        >
          <Puzzle.Board
            customLightSquareStyle={customLightSquareStyle}
            customDarkSquareStyle={customDarkSquareStyle}
            onMove={handleMove}
          />
        </div>
        <div className="puzzle-buttons-casual">
          <div className="user-rating">
            <h3>
              Rating: {userRating}{" "}
              {ratingChange !== 0 && (
                <span style={{ color: ratingChange > 0 ? "green" : "red" }}>
                  {ratingChange > 0 ? `+${ratingChange}` : ratingChange}
                </span>
              )}
            </h3>
          </div>
          <div className="puzzle-buttons-restart-next">
            <Puzzle.Reset
              asChild
              onReset={() => {
                setTimeout(() => {
                  setIsPuzzleCompleted(false);
                  setRatingChange(0);
                }, 0);
              }}
            >
              <button className="puzzle-button-restart-casual">Restart</button>
            </Puzzle.Reset>
            <Puzzle.Reset
              asChild
              puzzle={puzzles[(puzzleIndex + 1) % puzzles.length]}
              onReset={() => {
                setTimeout(() => {
                  setPuzzleIndex((puzzleIndex + 1) % puzzles.length);
                  setRatingChange(0);
                  setIsPuzzleRated(false);
                  sessionStorage.removeItem("moveHistory");
                  sessionStorage.removeItem("currentMoveIndex");
                }, 0);
              }}
            >
              <button className="puzzle-button-restart-casual">Next</button>
            </Puzzle.Reset>
            <Puzzle.Hint>Hint</Puzzle.Hint>
          </div>
          {isPuzzleCompleted && (
            <div className="puzzle-buttons-restart-next">
              <button
                className="puzzle-button-restart-casual"
                onClick={() => navigate("/PuzzleAnalysis")}
              >
                Analyze
              </button>
            </div>
          )}
        </div>
      </Puzzle.Root>
    </div>
  );
};
